import {
  Button,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from "antd"
import { PlusOutlined } from "@ant-design/icons"
import usePlatform from "contexts/platform/use-platform"
import { ICompany, IOpusUser } from "platform-client/types"
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { handleValidationErrors } from "utils/pocketknife/handle-validation-errors"
import { useBrokerageOffices } from "../../hooks/use-brokerage-offices"
import { canWorkInSeveralBranches } from "../../utils/multi-branch-roles"

/**
 * Manages the offices (branches) a user belongs to. Offices are added and removed locally,
 * and only saved when the user clicks Save. A user must always belong to at least one office
 */
const ManageOfficesModal: React.FC<IManageOfficesModalProps> = ({
  visible,
  user,
  company,
  isLoading = false,
  onClose
}) => {
  const platform = usePlatform()
  const queryClient = useQueryClient()
  const [officeIDs, setOfficeIDs] = useState<string[]>([])
  const [officeToAdd, setOfficeToAdd] = useState<string>()

  const userBranches = useQuery(
    ["userBranches", user?.id],
    () => platform.user.getUserBranches(user?.id as string),
    { enabled: visible && !!user?.id }
  )

  const offices = useBrokerageOffices(visible ? company : undefined)

  const savedOfficeIDs = useMemo(
    () => (userBranches.data || []).map((branch) => branch.company.id),
    [userBranches.data]
  )

  // start from the user's saved offices each time the modal opens
  useEffect(() => {
    if (visible) {
      setOfficeIDs(savedOfficeIDs)
      setOfficeToAdd(undefined)
    }
  }, [visible, savedOfficeIDs])

  const officesToAdd = officeIDs.filter((id) => !savedOfficeIDs.includes(id))
  const officesToRemove = savedOfficeIDs.filter((id) => !officeIDs.includes(id))
  const hasChanges = officesToAdd.length > 0 || officesToRemove.length > 0

  const save = useMutation(
    async () => {
      // add before removing, so the user always belongs to at least one office
      for (const companyID of officesToAdd) {
        await platform.user.addUserToBranch(user?.id as string, companyID)
      }
      for (const companyID of officesToRemove) {
        await platform.user.removeUserFromBranch(user?.id as string, companyID)
      }
    },
    {
      onSuccess: () => onClose(),
      onError: (error: AxiosError) => handleValidationErrors(error),
      // some of the changes may have saved before an error
      onSettled: () => {
        queryClient.invalidateQueries(["userBranches", user?.id])
        queryClient.invalidateQueries("listBrokerUsers")
      }
    }
  )

  const officeName = (id: string) =>
    offices.data?.find((office) => office.id === id)?.name ||
    userBranches.data?.find((branch) => branch.company.id === id)?.company
      .name ||
    id

  const defaultOfficeID = userBranches.data?.find((branch) => branch.isDefault)
    ?.company.id

  // a user can't leave an office while they own clients in it
  const ownedClientCount = (officeID: string) =>
    userBranches.data?.find((branch) => branch.company.id === officeID)
      ?.ownedClientCount ?? 0

  const removalBlockedReason = (officeID: string) => {
    const clients = ownedClientCount(officeID)

    if (clients > 0) {
      return `${user?.fullName || "This user"} owns ${clients} ${
        clients === 1 ? "client" : "clients"
      } in this office, so can't be removed from it`
    }

    return officeIDs.length <= 1
      ? "A user must belong to at least one office"
      : undefined
  }

  const isLastOffice = officeIDs.length <= 1
  const canAddOffice = canWorkInSeveralBranches(user)
  // users can only be added to the brokerage's branches, not its top level (parent) company
  const topLevelCompanyID = company?.parentCompanyID || company?.id
  const availableOffices = (offices.data || []).filter(
    (office) =>
      office.id !== topLevelCompanyID && !officeIDs.includes(office.id)
  )

  return (
    <Modal
      title={`Manage offices${user?.fullName ? ` - ${user.fullName}` : ""}`}
      visible={visible}
      okText="Save"
      okButtonProps={{
        disabled: !hasChanges || officeIDs.length === 0,
        loading: save.isLoading
      }}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnClose
    >
      <Spin spinning={isLoading || userBranches.isLoading}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text strong>Offices</Typography.Text>
          <div data-testid="user-offices">
            {officeIDs.map((id) => (
              <Tooltip key={id} title={removalBlockedReason(id)}>
                <Tag
                  closable={
                    !isLastOffice && !save.isLoading && !ownedClientCount(id)
                  }
                  onClose={(e) => {
                    e.preventDefault()
                    setOfficeIDs((ids) =>
                      ids.filter((officeID) => officeID !== id)
                    )
                  }}
                >
                  {officeName(id)}
                  {id === defaultOfficeID && " (Default)"}
                </Tag>
              </Tooltip>
            ))}
          </div>
          {canAddOffice ? (
            availableOffices.length > 0 && (
              <Space>
                <Select
                  aria-label="Add office"
                  placeholder="Add to office"
                  style={{ minWidth: 220 }}
                  value={officeToAdd}
                  onChange={(value) => setOfficeToAdd(value as string)}
                  options={availableOffices.map((office) => ({
                    label: office.name,
                    value: office.id
                  }))}
                />
                <Button
                  icon={<PlusOutlined />}
                  disabled={!officeToAdd || save.isLoading}
                  onClick={() => {
                    if (officeToAdd) {
                      setOfficeIDs((ids) => [...ids, officeToAdd])
                      setOfficeToAdd(undefined)
                    }
                  }}
                >
                  Add
                </Button>
              </Space>
            )
          ) : (
            <Typography.Text type="secondary">
              Only account managers and users can belong to more than one
              office.
            </Typography.Text>
          )}
        </Space>
      </Spin>
    </Modal>
  )
}

interface IManageOfficesModalProps {
  visible: boolean
  /** the user, including their roles */
  user: IOpusUser | undefined
  /** the office being viewed, used to find the other offices of the brokerage */
  company: Pick<ICompany, "id" | "parentCompanyID"> | undefined
  isLoading?: boolean
  onClose: () => void
}

export default ManageOfficesModal
